// Shared WebLLM `model_lib` rewrite — used on BOTH the main thread (client.ts)
// AND inside the WebLLM web worker (worker.ts).
//
// Why it must run in the worker:
// WebLLM's `WebWorkerMLCEngine` only forwards a custom appConfig to the worker
// via a *fire-and-forget* `postMessage` (`setAppConfig`, never awaited — see
// node_modules/@mlc-ai/web-llm lib/index.js). It races the awaited `reload`
// message. If `reload` wins, the worker's engine falls back to
// `prebuiltAppConfig`, whose `model_lib` points at a third-party *code* CDN
// that our CSP `connect-src` + the service-worker allowlist hard-block. The
// model *weights* (Hugging Face / Xet, allow-listed) still download — so the
// UI shows "loaded" — but the compiled WebGPU kernel never loads, and every
// completion throws `ModelNotLoadedError`. Setting this appConfig directly on
// the worker handler's engine (worker.ts) eliminates the race deterministically.

export interface ModelRecordLike {
  model: string;
  model_id: string;
  model_lib: string;
  [k: string]: unknown;
}

export interface AppConfigLike {
  model_list: ModelRecordLike[];
  [k: string]: unknown;
}

// Keep in sync with SUPPORTED_MODELS in ./types and scripts/fetch-webllm-libs.cjs.
export const LOCAL_LIB_MODEL_IDS = new Set([
  'Llama-3.1-8B-Instruct-q4f16_1-MLC',
  'Phi-3.5-mini-instruct-q4f16_1-MLC',
  'gemma-2-9b-it-q4f16_1-MLC',
]);

/**
 * Rewrite each supported model's `model_lib` so the compiled WebGPU kernel
 * loads from our own origin (a static asset under `/webllm-libs/`, CSP-
 * permitted as 'self') instead of the third-party code CDN the prebuilt
 * config points at (blocked by connect-src — PRD §6.6 / ADR-0004). Kernels
 * are bundled at build time by scripts/fetch-webllm-libs.cjs into
 * `public/webllm-libs/`, served at `${BASE_URL}webllm-libs/<file>`. Model
 * *weights* still stream from Hugging Face / Xet — unchanged.
 */
export function buildLocalAppConfig(prebuilt: AppConfigLike): AppConfigLike {
  const base = import.meta.env.BASE_URL; // '/' on Cloudflare Pages root
  return {
    ...prebuilt,
    model_list: prebuilt.model_list.map((rec) => {
      if (!LOCAL_LIB_MODEL_IDS.has(rec.model_id)) return rec;
      const file = rec.model_lib.split('/').pop();
      if (!file) return rec;
      return {
        ...rec,
        model_lib: new URL(`${base}webllm-libs/${file}`, self.location.origin).href,
      };
    }),
  };
}
